/**
 * Seed data — VERBATIM from the source component (component.js:68-139).
 * Every value (id, name, purpose, color, versions, products, conf, note) is an
 * exact copy. Do not "improve" these strings; they are the source of truth.
 */
import type { Disc, Integration, SchemaField, Tier } from '../domain/types';

/** Palette for newly-ingested adapters (cycled by adapters.length). */
export const COLORS = ['#b45309', '#7c3aed', '#0369a1', '#be123c', '#15803d', '#c2410c'];

/** Ingestion progress step labels. */
export const STEP_LABELS = [
  'Fetching source',
  'Building structured vendor model',
  'Matching against catalog v1',
  'Drafting mapping + confidence',
];

/** Outbound webhook event types. */
export const OUT_EVENTS = ['created', 'updated', 'deleted'];

/** Product-type categories (directory grouping + ingest selector). */
export const CATEGORIES = [
  'CRM',
  'Scheduling',
  'Messaging',
  'Email',
  'Payments',
  'Support',
  'Practice mgmt',
  'Analytics',
  'Other',
];

/** The catalog of built-in connectors. */
export const INTS: Integration[] = [
  {
    id: 'google-calendar', name: 'Google Calendar', purpose: 'Two-way calendar sync and live availability.',
    cls: 'mcp', color: '#1a73e8', initials: 'GC', versions: ['v3', 'v2'], change: 'webhook', category: 'Scheduling', company: 'Google',
    products: [{ key: 'Calendar', dataType: 'scheduling' }, { key: 'Hire', dataType: 'scheduling' }],
  },
  {
    id: 'ezyvet', name: 'ezyVet', purpose: 'Practice management — booking, clients, and patients.',
    cls: 'custom', color: '#0a8a87', initials: 'EZ', versions: ['2.7', '2.6'], change: 'webhook', category: 'Practice mgmt', company: 'ezyVet',
    products: [{ key: 'Calendar', dataType: 'scheduling' }, { key: 'Hire', dataType: 'scheduling' }, { key: 'CRM', dataType: 'contacts' }],
  },
  {
    id: 'gmail', name: 'Gmail', purpose: 'Send and receive email on the tenant domain.',
    cls: 'mcp', color: '#ea4335', initials: 'GM', versions: ['v1'], change: 'poll', category: 'Email', company: 'Google',
    products: [{ key: 'Tickets', dataType: 'messaging' }, { key: 'CRM', dataType: 'messaging' }, { key: 'Hire', dataType: 'messaging' }],
  },
  {
    id: 'slack', name: 'Slack', purpose: 'Team messaging, alerts, and notifications.',
    cls: 'mcp', color: '#611f69', initials: 'SL', versions: ['v2'], change: 'webhook', category: 'Messaging', company: 'Salesforce',
    products: [{ key: 'Tickets', dataType: 'messaging' }, { key: 'People', dataType: 'messaging' }, { key: 'Hire', dataType: 'messaging' }],
  },
  {
    id: 'stripe', name: 'Stripe', purpose: 'Payments, invoices, and subscriptions.',
    cls: 'openapi', color: '#635bff', initials: 'ST', versions: ['2024-06'], change: 'poll', category: 'Payments', company: 'Stripe',
    products: [{ key: 'CRM', dataType: 'payments' }, { key: 'People', dataType: 'payments' }],
  },
  {
    id: 'zendesk', name: 'Zendesk', purpose: 'Customer support tickets and contacts.',
    cls: 'openapi', color: '#03363d', initials: 'ZD', versions: ['v2'], change: 'poll', category: 'Support', company: 'Zendesk',
    products: [{ key: 'Tickets', dataType: 'tickets' }, { key: 'CRM', dataType: 'contacts' }],
  },
  {
    id: 'hubspot', name: 'HubSpot', purpose: 'CRM contacts, deals, and pipelines.',
    cls: 'openapi', color: '#ff7a59', initials: 'HS', versions: ['v3'], change: 'poll', category: 'CRM', company: 'HubSpot',
    products: [{ key: 'CRM', dataType: 'contacts' }, { key: 'Hire', dataType: 'contacts' }],
  },
  {
    id: 'twilio', name: 'Twilio', purpose: 'SMS and voice notifications.',
    cls: 'openapi', color: '#f22f46', initials: 'TW', versions: ['2010-04'], change: 'poll', category: 'Messaging', company: 'Twilio',
    products: [{ key: 'Tickets', dataType: 'messaging' }, { key: 'People', dataType: 'messaging' }],
  },
];

/** Field-resolution discrepancies (polymorphic / ambiguous mappings). */
export const DISC: Record<string, Disc> = {
  subject: {
    title: 'Subject is polymorphic — {type, ref}',
    edgeLabel: 'subject_ref → vendor target',
    desc: 'Canonical Subject resolves to different ezyVet fields depending on Subject.type. Pick the primary target.',
    candidates: [
      { label: 'consult.animal_id', conf: '61%', note: "when type = 'patient'" },
      { label: 'consult.contact_id', conf: '33%', note: "when type = 'client'" },
      { label: 'consult.resource_id', conf: '18%', note: 'rarely used' },
    ],
  },
  name: {
    title: 'Saw `name` — multiple canonical targets',
    edgeLabel: 'vendor `name`',
    desc: 'ezyVet sends a flat `name`. The canonical model splits this across entities. Choose the canonical field.',
    candidates: [
      { label: 'Contact.display_name', conf: '55%', note: 'best match for booking party' },
      { label: 'Employee.full_name', conf: '41%', note: 'if staff-owned' },
      { label: 'Party.legal_name', conf: '22%', note: 'formal records only' },
    ],
  },
};

/** Vendor schema (target for outbound mapping search). */
export const VENDOR_SCHEMA: SchemaField[] = [
  { label: 'consult.contact_id', type: 'int' },
  { label: 'consult.animal_id', type: 'int' },
  { label: 'consult.resource_id', type: 'int' },
  { label: 'consult.start_time', type: 'ISO-8601' },
  { label: 'consult.end_time', type: 'ISO-8601' },
  { label: 'consult.duration_minutes', type: 'int (min)' },
  { label: 'consult.resource', type: 'string' },
  { label: 'consult.location', type: 'string' },
  { label: 'consult.description', type: 'string' },
  { label: 'consult.status', type: 'enum' },
  { label: 'consult.type', type: 'enum' },
  { label: 'consult.created_by', type: 'int' },
];

/** Canonical schema (target for inbound mapping search). */
export const CANON_SCHEMA: SchemaField[] = [
  { label: 'Engagement.external_ref', type: 'ref' },
  { label: 'Engagement.status', type: 'enum' },
  { label: 'Engagement.start_at', type: 'timestamptz' },
  { label: 'Engagement.end_at', type: 'timestamptz' },
  { label: 'Engagement.location', type: 'text' },
  { label: 'Engagement.notes', type: 'text' },
  { label: 'Contact.display_name', type: 'string' },
  { label: 'Contact.email', type: 'string' },
  { label: 'Employee.full_name', type: 'string' },
  { label: 'Party.legal_name', type: 'string' },
  { label: 'Subject.ref', type: 'ref' },
];

/** Ingestion tiers — confidence + draft metadata by source type. */
export const TIERS: Record<'openapi' | 'prose', Tier> = {
  openapi: {
    tier: 'API spec', label: 'API spec · structured',
    note: 'Structured spec. High-confidence draft — a handful of ambiguous fields will need a one-click pick.',
    tone: 'info', cls: 'openapi', source: 'ingested from API spec', conf: '90%', edges: '11 / 13', needs: 2,
    auth: 'OAuth2 · client-credentials', change: 'poll', ph: 'https://api.vendor.com/openapi.json', urlLabel: 'API spec URL',
  },
  prose: {
    tier: 'API docs', label: 'API docs · unstructured',
    note: 'Unstructured. Strong first draft only — the reviewer reviews rather than rubber-stamps.',
    tone: 'warn', cls: 'custom', source: 'ingested from API docs', conf: '78%', edges: '9 / 14', needs: 5,
    auth: 'API key', change: 'poll', ph: 'https://docs.vendor.com/api', urlLabel: 'Docs URL',
  },
};
